﻿import useTheme from "../../hooks/useTheme";
import useSortData from "../../hooks/useSortData";
import { BiFilter, BiCog, BiChevronDown, BiChevronUp, BiExpandAlt, BiSearchAlt } from 'react-icons/bi';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Form, Label, Input, Row, Button, Col, Table, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from "reactstrap";
import DataFilter from './dataFilter';
import DataLoaderFailed from './dataLoaderFailed';
import DataLoader from './dataLoader';
import debounce from 'lodash/debounce';
// Vite pre-bundles react-windowed-select as CJS without unwrapping the default
// export, so the bare import resolves to `{ default: Component, … }` — rendering
// that object as a component throws React's "got: object" error. Unwrap manually.
import WindowedSelectModule from 'react-windowed-select';
const WindowedSelect = WindowedSelectModule.default ?? WindowedSelectModule;
import { customSelectTheme, selectStyle } from './selectStyling';

export default function DataTable({ tableName, columns, data, isLoading, error, onRowClick, onFilterChange, totalRecords, sortConfiguration = { key: columns[0]?.key, direction: "ascending" }, filters = [], actions = [], showSettings, visibleColumns, setVisibleColumns, defaultVisibleColumns, settingsColumns, filter, showSearch = true, showPagination = true }) {
    const theme = useTheme();
    const customTheme = customSelectTheme(theme);
    const { sortBy, sortConfig } = useSortData(data, sortConfiguration);
    const [searchText, setSearchText] = useState(
        () => filter?.searchOptions?.searchTerm ?? ""
    );

    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(filter.pageNumber);
    const [pageSize, setPageSize] = useState(filter.pageSize);
    const numPerPageOptions = [10, 20, 50, 100];

    // `value` is the dotted property path the backend uses to resolve the
    // column via reflection (e.g. "customer.name"). Falls back to `key` for
    // plain columns where key and path are the same.
    const searchFieldOptions = (settingsColumns ?? [])
        .filter(col => !col.system)
        .map(col => ({ value: col.path ?? col.key, label: col.label }));

    // Default set used by the Reset button below — same derivation the page
    // uses to seed its initial filter from localStorage. Local derivation is
    // fine: it's cheap, and keeping it here means the page doesn't have to
    // recompute / pass it in just for a reset action.
    const defaultSearchableFields = (settingsColumns ?? [])
        .filter(col => !col.system && defaultVisibleColumns?.[col.key])
        .map(col => col.path ?? col.key);

    // Source of truth for "which fields are searched" lives on the page's
    // `filter.searchOptions.searchFields`. We used to keep a second copy in
    // local state here, seed it from localStorage, and push it upward on
    // mount — which caused every page to fire two back-to-back queries (one
    // with empty fields, one with the persisted fields). Reading straight
    // from the filter prop eliminates that — the page reads localStorage in
    // its useState initializer so the first query already has the right
    // value. LocalStorage persistence still happens on change (below).
    const searchableFields = filter?.searchOptions?.searchFields ?? [];

    const mountedStyle = { animation: 'inAnimation 250ms ease-in' };
    const unmountedStyle = { animation: 'outAnimation 250ms ease-out', animationFillMode: 'forwards' };
    const totalPages = Math.ceil(totalRecords / pageSize);

    const toggleSettingsModal = () => setSettingsModalOpen((prev) => !prev);

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        onFilterChange({ filterId: 'pageSize', value: e.target.value });
        setCurrentPage(1);
    }

    const handleCurrentPageChange = (e, page) => {
        if (totalPages === 0) return;
        e.preventDefault();
        onFilterChange({ filterId: 'pageNumber', value: page });
        setCurrentPage(page);
    }

    const handleSortOptionsChange = () => {
        onFilterChange({ filterId: 'sortOptions', value: sortConfig });
        setCurrentPage(1);
    }

    const onFilterChangeRef = useRef(onFilterChange);
    useEffect(() => { onFilterChangeRef.current = onFilterChange; }, [onFilterChange]);

    const debouncedSearch = useMemo(() => debounce((value) => {
        onFilterChangeRef.current({ filterId: 'search', value });
        setCurrentPage(1);
    }, 500), []);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        debouncedSearch(e.target.value);
    }

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch])

    const hasMounted = useRef(false);

    useEffect(() => {
        if (hasMounted.current) {
            handleSortOptionsChange();
        } else {
            hasMounted.current = true;
        }
    }, [sortConfig]);

    if (error) return <DataLoaderFailed />;
    if (isLoading) return <DataLoader />;

    return (
        <>
            <div className="surface">
            <Container fluid className={"px-0".concat(filtersOpen ? " pb-2" : "")}>
                {(showSearch || showSettings || filters.length > 0 || actions.length > 0) &&
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ minHeight: '40px' }}>
                        {showSearch &&
                            // flex basis 220px lets the search shrink when
                            // sharing the row with filter / cog buttons and
                            // wrap full-width on mobile. minWidth:0 defeats
                            // the default flex min-content floor that would
                            // otherwise keep the input from ever shrinking.
                            <Form style={{ flex: '1 1 220px', minWidth: 0 }} onSubmit={(e) => e.preventDefault()}>
                                <Input
                                    type="search"
                                    id="search"
                                    placeholder={(() => {
                                        const labels = searchableFields
                                            .map(key => searchFieldOptions.find(o => o.value === key)?.label)
                                            .filter(Boolean);
                                        const fieldText = labels.length === 0 ? ''
                                            : labels.length === 1 ? ` on ${labels[0]}`
                                            : ` on ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
                                        return `Search ${totalRecords} records${fieldText}...`;
                                    })()}
                                    onChange={handleSearchChange}
                                    value={searchText}
                                />
                            </Form>
                        }
                        {filters.filter(f => f.advanced !== undefined && f.advanced === false).length > 0 &&
                            <div className="d-flex gap-2 flex-wrap align-items-center" style={{ flexDirection: 'row' }}>
                                {filters.filter(f => f.advanced !== undefined && f.advanced === false).map((filter, index) => {
                                    return (
                                        <DataFilter
                                            key={index}
                                            id={filter.id}
                                            label={filter.label}
                                            type={filter.type}
                                            placeholder={filter.placeholder}
                                            isSearchable={filter.isSearchable}
                                            options={filter.options}
                                            value={filter.type !== 'select' ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                            onChange={(e) => onFilterChange({ filterId: filter.id, value: e })}
                                            isInvalid={filter.isInvalid}
                                            defaultValue={filter.defaultValue}
                                            step={filter.step}
                                            isDisabled={filter.isDisabled}
                                            isClearable={filter.isClearable}
                                            advanced={filter.advanced}
                                        />
                                    );
                                })}
                            </div>
                        }
                        {filters.filter(f => f.advanced === undefined || f.advanced === true).length > 0 && <Button size="sm" outline={!filtersOpen} color="secondary" className="d-inline-flex align-items-center justify-content-center" style={{ width: '31px', height: '31px', padding: 0 }} onClick={() => setFiltersOpen((prev) => !prev)}>
                            <BiFilter size={14} />
                        </Button>}
                        <div style={{ flexGrow: 1 }} />
                        <div className="d-flex gap-2 align-items-center">
                            {actions.map((action, index) => (<div key={index}>{action}</div>))}
                            {showSettings &&
                                <Button size="sm" outline color="secondary" className="d-inline-flex align-items-center justify-content-center" style={{ width: '31px', height: '31px', padding: 0 }} onClick={toggleSettingsModal}>
                                    <BiCog size={14} />
                                </Button>
                            }
                        </div>
                    </div>
                }
            </Container>
            {filtersOpen &&
                <Row>
                    <Container fluid style={filtersOpen ? mountedStyle : unmountedStyle} className="contrast-dropdown py-3 transitionDiv">
                        <div style={{ minHeight: '40px', display: 'flex', flexDirection: 'row' }}>
                            <Col sm={6}>
                                {filters.filter(f => f.advanced === undefined || f.advanced === true).map((filter, index) => {
                                    return (index % 2 === 0 && <DataFilter
                                        key={index}
                                        id={filter.id}
                                        label={filter.label}
                                        type={filter.type}
                                        placeholder={filter.placeholder}
                                        isSearchable={filter.isSearchable}
                                        options={filter.options}
                                        value={filter.type !== 'select' ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                        onChange={(e) => onFilterChange({ filterId: filter.id, value: e })}
                                        isInvalid={filter.isInvalid}
                                        defaultValue={filter.defaultValue}
                                        step={filter.step}
                                        isDisabled={filter.isDisabled}
                                        isClearable={filter.isClearable}
                                        advanced={filter.advanced}
                                    />);
                                })}
                            </Col>
                            <Col sm={6}>
                                {filters.filter(f => f.advanced === undefined || f.advanced === true).map((filter, index) => {
                                    return (index % 2 === 1 && <DataFilter
                                        key={index}
                                        id={filter.id}
                                        label={filter.label}
                                        type={filter.type}
                                        placeholder={filter.placeholder}
                                        isSearchable={filter.isSearchable}
                                        options={filter.options}
                                        value={filter.type !== 'select' ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                        onChange={(e) => onFilterChange({ filterId: filter.id, value: e })}
                                        isInvalid={filter.isInvalid}
                                        defaultValue={filter.defaultValue}
                                        step={filter.step}
                                        isDisabled={filter.isDisabled}
                                        isClearable={filter.isClearable}
                                        advanced={filter.advanced}
                                    />);
                                })}
                            </Col>
                        </div>
                    </Container>
                </Row>
            }
            <Container fluid className="px-0 pt-2">
                <div
                    className="table-responsive dataTableScroll"
                    style={{
                        maxHeight: filtersOpen ? '45vh' : '60vh',
                        overflowX: 'auto'
                    }}
                >
                    <Table
                        className="mt-3 align-middle"
                        hover
                        // min-width keeps columns readable on mobile; the
                        // parent .dataTableScroll has overflowX: auto, so
                        // narrow viewports swipe horizontally instead of
                        // compressing every column into an unreadable stub.
                        style={{ tableLayout: 'fixed', width: '100%', minWidth: '720px' }}
                    >
                        <thead className="sticky-top">
                            <tr>
                                {columns.map((col) => {
                                    // Sort key is the dotted property path the backend
                                    // resolves via reflection (e.g. "customer.name"),
                                    // falling back to `col.key` for flat columns.
                                    const sortKey = col.path ?? col.key;
                                    return (
                                    <th className={col.className} key={col.key} style={{ cursor: !col.disableSorting && 'pointer', ...(col.style || {}) }} onClick={() => { if (!col.disableSorting) sortBy(sortKey) }}>
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            {col.label}
                                            &nbsp;
                                            {sortConfig?.key === sortKey && !col.disableSorting &&
                                                (sortConfig?.direction === 'descending'
                                                    ? <BiChevronDown data-testid="sort-icon" />
                                                    : <BiChevronUp data-testid="sort-icon" />)
                                            }
                                            {col.disableSorting && <BiExpandAlt data-testid="sort-icon" size={14} style={{ opacity: 0.4 }} />}
                                        </span>
                                    </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && data.length <= 0 &&
                                <tr className="no-data-row">
                                    <td colSpan={columns.length}>
                                        <div className="container-fluid text-center my-5 py-3">
                                            <BiSearchAlt className="display-1 mb-2 text-blue" />
                                            <h1 className="display-5 fw-bold px-2 mt-2 mb-0">No results</h1>
                                            <p className="fs-3 px-2">There's no data to display.</p>
                                        </div>
                                    </td>
                                </tr>
                            }
                            {!isLoading && data.length > 0 && data.map((item, index) => (
                                <tr key={index} className={onRowClick ? 'cursor-pointer' : ''} onClick={(e) => onRowClick && onRowClick(e, item)} style={onRowClick ? { cursor: 'pointer' } : {}}>
                                    {columns.map((col) => (
                                        <td key={col.key} style={{ wordBreak: 'break-word', ...(col.style || {}) }}>
                                            {col.render ? col.render(item[col.key], item) : item[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                {showPagination &&
                    // Cluster the pagination block and the page-size select on
                    // one row with gap-3 between them (not justify-between —
                    // that left a huge empty gap on wide surfaces and made the
                    // select feel disconnected). Stack only below sm (576px —
                    // genuinely phone-only); tablets and up keep the row.
                    <Row className="mt-3 g-2 align-items-center">
                        <Col xs={12} sm className="d-flex align-items-center flex-wrap">
                            {/* listClassName targets the inner <ul>; reactstrap's
                                top-level `className` lands on the wrapping
                                <nav>, so mb-0 there doesn't kill the <ul>'s
                                default 1rem bottom margin that was offsetting
                                the 'Page X of Y' sibling vertically. */}
                            <Pagination size="sm" listClassName="mb-0">
                                <PaginationItem disabled={currentPage === 1 || data.length <= 0}>
                                    <PaginationLink first href="#" onClick={(e) => handleCurrentPageChange(e, 1)} />
                                </PaginationItem>
                                <PaginationItem disabled={currentPage === 1 || data.length <= 0}>
                                    <PaginationLink previous href="#" onClick={(e) => handleCurrentPageChange(e, currentPage - 1)} />
                                </PaginationItem>
                                <>
                                    {(() => {
                                        const pages = [];

                                        const createPage = (num) => (
                                            <PaginationItem key={num} active={currentPage === num}>
                                                <PaginationLink href="#" onClick={(e) => { e.preventDefault(); handleCurrentPageChange(e, num) }}>{num}</PaginationLink>
                                            </PaginationItem>
                                        )

                                        if (totalPages <= 4) {
                                            for (let i = 1; i <= totalPages; i++)
                                                pages.push(createPage(i));
                                        } else {
                                            let startPage = currentPage;

                                            if (startPage > totalPages - 3)
                                                startPage = totalPages - 3;

                                            for (let i = startPage; i < startPage + 3; i++)
                                                pages.push(createPage(i));

                                            if (startPage + 3 <= totalPages) {
                                                if (startPage + 3 === totalPages)
                                                    pages.push(createPage(totalPages));
                                                else
                                                    pages.push(
                                                        <PaginationItem key="ellipsis" disabled>
                                                            <PaginationLink href="#">...</PaginationLink>
                                                        </PaginationItem>
                                                    );
                                            }
                                        }

                                        return pages;
                                    })}
                                </>
                                <PaginationItem disabled={currentPage === totalPages || data.length <= 0}>
                                    <PaginationLink next href="#" onClick={(e) => handleCurrentPageChange(e, currentPage + 1)} />
                                </PaginationItem>
                                <PaginationItem disabled={currentPage === totalPages || data.length <= 0}>
                                    <PaginationLink last href="#" onClick={(e) => handleCurrentPageChange(e, totalPages)} />
                                </PaginationItem>
                            </Pagination>
                            <span className="text-muted small ps-3">
                                {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : "No records found"}
                            </span>
                        </Col>
                        <Col xs={12} sm="auto" className="d-flex justify-content-end align-items-center gap-2">
                            <span className="text-muted small">Rows per page</span>

                            <Input
                                id="pageSizeSelect"
                                name="select"
                                type="select"
                                value={pageSize}
                                style={{ width: '5rem' }}
                                bsSize="sm"
                                onChange={handlePageSizeChange}
                            >
                                {numPerPageOptions.map((num, index) => (
                                    <option key={index} value={num}>{num}</option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                }
            </Container>
            </div>
            <Modal isOpen={settingsModalOpen} toggle={toggleSettingsModal} centered backdrop="static">
                <ModalHeader toggle={toggleSettingsModal}>Table Settings</ModalHeader>
                <ModalBody>
                    {settingsColumns.map((col) => (
                        <FormGroup switch key={col.key} className="d-flex gap-2">
                            <Input
                                type="switch"
                                role="switch"
                                id={`switch-${col.key}`}
                                checked={visibleColumns[col.key]}
                                disabled={col.system}
                                onChange={(e) => {
                                    const updated = {
                                        ...visibleColumns,
                                        [col.key]: e.target.checked
                                    };
                                    setVisibleColumns(updated);
                                    localStorage.setItem(tableName, JSON.stringify(updated))
                                }}
                            />
                            <Label check htmlFor={`switch-${col.key}`}>
                                {col.label || "Action"} {col.system && <small className="text-muted">(Required)</small>}
                            </Label>
                        </FormGroup>
                    ))}
                    {searchFieldOptions.length > 0 && (
                        <>
                            <hr />
                            <Label className="fw-semibold small">Search Fields</Label>
                            <WindowedSelect
                                isMulti
                                classNamePrefix="data-select"
                                options={searchFieldOptions}
                                value={searchFieldOptions.filter(opt => searchableFields.includes(opt.value))}
                                onChange={(values) => {
                                    const updated = (values ?? []).map(v => v.value);
                                    localStorage.setItem(`${tableName}_search`, JSON.stringify(updated));
                                    onFilterChangeRef.current?.({ filterId: 'searchFields', value: updated });
                                }}
                                styles={selectStyle}
                                theme={customTheme}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                placeholder="Select fields to search..."
                            />
                        </>
                    )}
                    <br />
                    <Button outline color="danger" size="sm" onClick={() => {
                        setVisibleColumns(defaultVisibleColumns);
                        localStorage.setItem(tableName, JSON.stringify(defaultVisibleColumns));
                        localStorage.setItem(`${tableName}_search`, JSON.stringify(defaultSearchableFields));
                        onFilterChangeRef.current?.({ filterId: 'searchFields', value: defaultSearchableFields });
                    }}>Reset to Defaults</Button>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={toggleSettingsModal}>Close</Button>
                </ModalFooter>
            </Modal>
        </>
    );
}